import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, mergeClasses, tokens,
  Button, Input, Field, Dropdown, Option, TabList, Tab,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  Skeleton, SkeletonItem,
} from '@fluentui/react-components';
import { API_BASE } from '../config.js';
import { iconMap } from '../theme/icons';
import { Card, EmptyState } from '../theme/primitives';
import { stonePalette } from '../theme/brand';

const CheckIcon = iconMap.check;
const CompareIcon = iconMap.compare_arrows;
const CloseIcon = iconMap.close;
const SearchIcon = iconMap.search;
const SearchOffIcon = iconMap.search_off;
const FilterOffIcon = iconMap.filter_list_off;
const InfoIcon = iconMap.info;

const MDF_FINISHES = [
  { labelKey: 'plate_comparison.finish_matte_2',        key: 'price_matte_2_side'       },
  { labelKey: 'plate_comparison.finish_matte_1',        key: 'price_matte_1_side'       },
  { labelKey: 'plate_comparison.finish_shiny',           key: 'price_shiny_1_side'       },
  { labelKey: 'plate_comparison.finish_super_matte',     key: 'price_super_shiny_1_side' },
  { labelKey: 'plate_comparison.finish_super_shiny',     key: 'price_super_shiny_2_side' },
];

const LMDP_FILTERS = [
  { key: 'color_tone',    labelKey: 'plate_comparison.filter_color_tone' },
  { key: 'color',         labelKey: 'plate_comparison.filter_color'      },
  { key: 'gloss',         labelKey: 'plate_comparison.filter_gloss'      },
  { key: 'roughness',     labelKey: 'plate_comparison.filter_roughness'  },
  { key: 'pattern',       labelKey: 'plate_comparison.filter_pattern'    },
  { key: 'manufacturer',  labelKey: 'plate_comparison.filter_manufacturer' },
];

const COMPARE_LABEL_KEYS = {
  code: 'plate_comparison.label_code', name: 'plate_comparison.label_name',
  color_tone: 'plate_comparison.filter_color_tone', color: 'plate_comparison.filter_color',
  gloss: 'plate_comparison.filter_gloss', roughness: 'plate_comparison.filter_roughness',
  pattern: 'plate_comparison.filter_pattern', manufacturer: 'plate_comparison.filter_manufacturer',
  thickness: 'plate_comparison.label_thickness', price: 'plate_comparison.label_price',
  price_matte_2_side: 'plate_comparison.compare_finish_matte_2', price_matte_1_side: 'plate_comparison.compare_finish_matte_1',
  price_shiny_1_side: 'plate_comparison.compare_finish_shiny', price_super_shiny_1_side: 'plate_comparison.compare_finish_super_matte',
  price_super_shiny_2_side: 'plate_comparison.compare_finish_super_shiny',
};

const getImagePath = (item, type) => {
  if (!item.image_1 && !item.image) return null;
  const name = item.image_1 || item.image;
  try {
    return type === 0
      ? require(`../assets/img/lmdp/${name}`)
      : require(`../assets/img/mdf/${name}`);
  } catch { return null; }
};

const getUniqueOptions = (data, key) =>
  [...new Set(data.map(i => i[key]).filter(v => v != null && v !== ''))].sort();

const useStyles = makeStyles({
  page: { minHeight: '100vh', backgroundColor: stonePalette[50] },

  headerBar: { backgroundColor: tokens.colorNeutralBackground1, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  container: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  title: { fontSize: tokens.fontSizeBase600, lineHeight: '1.2', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, letterSpacing: '-0.01em', margin: `0 0 ${tokens.spacingVerticalXXS}` },
  subtitle: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, margin: 0 },

  selectedRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalM, flexWrap: 'wrap' },
  selectedLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    paddingLeft: tokens.spacingHorizontalS, paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXXS, paddingBottom: tokens.spacingVerticalXXS,
    borderRadius: tokens.borderRadiusCircular, border: 'none',
  },
  // Realus Fluent <Button> — tik spalvos paveldėjimas iš savo (žalio) chip
  // fono ir kompaktiškas dydis, kad tilptų mažoje pilulėje (žr. #5).
  chipCloseBtn: {
    minWidth: 'unset', width: '16px', height: '16px', padding: 0, color: 'inherit',
  },

  body: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },

  tabList: { marginBottom: tokens.spacingVerticalL, width: 'fit-content' },

  filtersCard: { marginBottom: tokens.spacingVerticalL },
  filtersRow: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM, alignItems: 'flex-end' },
  searchField: { flex: 1, minWidth: '12rem' },
  filterField: { minWidth: '9rem' },
  mdfField: { minWidth: '12rem' },
  resetBtn: { flexShrink: 0 },

  compareCard: {
    marginBottom: tokens.spacingVerticalL, padding: tokens.spacingHorizontalL,
    border: `2px solid ${tokens.colorBrandStroke2}`, backgroundColor: tokens.colorBrandBackground2,
  },
  compareHeader: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalM },
  compareTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, margin: 0 },
  compareCount: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  tableScroll: { overflowX: 'auto' },
  table: { minWidth: '400px', backgroundColor: 'transparent' },
  headCell: { minWidth: '140px', fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2 },
  headCellItem: { minWidth: '120px' },
  headCellItemInner: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: tokens.spacingHorizontalXXS },
  headCode: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold },
  bodyCellLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground2 },
  bodyCellValue: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground1, textAlign: 'center' },

  statusLine: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalM },
  statusText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, margin: 0 },
  maxNotice: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalS, paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS, paddingBottom: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorStatusWarningBackground1, border: `1px solid ${tokens.colorStatusWarningBorder1}`,
    borderRadius: tokens.borderRadiusXLarge, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium,
    color: tokens.colorStatusWarningForeground1,
  },

  // repeat(auto-fill, minmax(...)) vietoj kelių @media laiptelių ant TOS
  // PAČIOS savybės — Griffel runtime negarantuoja tvarkos tarp jų be
  // build-time plugino, tad plačiuose languose laimėdavo ne didžiausias
  // breakpoint'as (žr. Home.jsx categoriesGrid/Footer.jsx grid komentarus) —
  // čia tai reiškė, kad prie 1280px+ vis tiek likdavo tik 5 stulpeliai vietoj
  // 10 (žr. /admin/bugs "eiluteje turi buti 10 blokeliu vietoj 5").
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: tokens.spacingHorizontalM,
  },

  cardBase: {
    position: 'relative', cursor: 'pointer', borderRadius: tokens.borderRadiusXLarge,
    border: `2px solid ${tokens.colorNeutralStroke2}`, backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingHorizontalXS,
    transitionProperty: 'border-color, background-color, box-shadow', transitionDuration: tokens.durationNormal,
    ':hover': { borderColor: tokens.colorBrandStroke2, boxShadow: tokens.shadow4 },
  },
  cardSelected: {
    borderColor: tokens.colorCompoundBrandStroke, backgroundColor: tokens.colorBrandBackground2,
    ':hover': { borderColor: tokens.colorCompoundBrandStroke },
  },
  cardCheck: {
    position: 'absolute', top: tokens.spacingVerticalXS, right: tokens.spacingHorizontalXS,
    width: '20px', height: '20px', borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorCompoundBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, fontSize: '12px',
  },
  cardImageWrap: { aspectRatio: '1 / 1', borderRadius: tokens.borderRadiusLarge, overflow: 'hidden', backgroundColor: tokens.colorNeutralBackground3, marginBottom: tokens.spacingVerticalXS },
  cardImage: { width: '100%', height: '100%', objectFit: 'cover' },
  cardImageFallback: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardImageFallbackText: { fontSize: '9px', color: tokens.colorNeutralForeground3, textAlign: 'center', paddingLeft: tokens.spacingHorizontalXXS, paddingRight: tokens.spacingHorizontalXXS, lineHeight: '1.2' },
  cardCode: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardName: { fontSize: '10px', color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardPrice: { fontSize: '11px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForeground2, marginTop: '2px' },

  skeletonCard: { borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden' },
  skeletonImage: { aspectRatio: '1 / 1', display: 'block' },
  skeletonLines: { padding: tokens.spacingHorizontalXS, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },

  emptyWrap: { paddingTop: '5rem', paddingBottom: '5rem' },
});

function PlateCard({ item, type, mdfFinish, selected, onSelect, onRemove }) {
  const s = useStyles();
  const img = getImagePath(item, type);
  const price = type === 1 ? item[mdfFinish] : item.price;

  return (
    <div
      onClick={() => (selected ? onRemove(item) : onSelect(item))}
      className={mergeClasses(s.cardBase, selected && s.cardSelected)}
    >
      {selected && (
        <div className={s.cardCheck}>
          <CheckIcon />
        </div>
      )}
      <div className={s.cardImageWrap}>
        {img
          ? <img src={img} alt={item.code} className={s.cardImage} />
          : (
            <div className={s.cardImageFallback}>
              <span className={s.cardImageFallbackText}>{item.code}</span>
            </div>
          )
        }
      </div>
      <div className={s.cardCode}>{item.code}</div>
      <div className={s.cardName}>{item.name}</div>
      {price != null && (
        <div className={s.cardPrice}>{price} €/m²</div>
      )}
    </div>
  );
}

function ComparePanel({ selected, type, mdfFinish, onRemove }) {
  const s = useStyles();
  const { t } = useTranslation();
  if (selected.length < 2) return null;

  const allKeys = type === 0
    ? ['code', 'name', 'color_tone', 'color', 'gloss', 'roughness', 'pattern', 'manufacturer', 'thickness', 'price']
    : ['code', 'name', 'thickness', ...MDF_FINISHES.map(f => f.key)];

  const visibleKeys = allKeys.filter(k => selected.some(i => i[k] != null && i[k] !== ''));

  return (
    <Card className={s.compareCard}>
      <div className={s.compareHeader}>
        <CompareIcon style={{ fontSize: 20, color: tokens.colorBrandForeground2 }} />
        <h3 className={s.compareTitle}>{t('plate_comparison.compare_title')}</h3>
        <span className={s.compareCount}>{t('plate_comparison.compare_count', { count: selected.length })}</span>
      </div>
      <div className={s.tableScroll}>
        <Table className={s.table} size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={s.headCell}>{t('plate_comparison.property')}</TableHeaderCell>
              {selected.map(item => (
                <TableHeaderCell key={item.code} className={mergeClasses(s.headCell, s.headCellItem)}>
                  <div className={s.headCellItemInner}>
                    <span className={s.headCode}>{item.code}</span>
                    <Button
                      appearance="transparent" size="small" icon={<CloseIcon style={{ fontSize: 13 }} />}
                      onClick={() => onRemove(item)} aria-label={t('plate_comparison.remove_aria', { code: item.code })}
                    />
                  </div>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleKeys.map((key) => (
              <TableRow key={key}>
                <TableCell className={s.bodyCellLabel}>{t(COMPARE_LABEL_KEYS[key]) || key}</TableCell>
                {selected.map(item => (
                  <TableCell key={item.code} className={s.bodyCellValue}>
                    {item[key] != null ? String(item[key]) : '–'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// Vieno pasirinkimo filtro Dropdown — ta pati struktūra, kaip
// FurnitureFilters.jsx FilterSortSelect (Option su value="" reiškia "Visi").
function FilterSelect({ label, value, options, onChange, className }) {
  const { t } = useTranslation();
  return (
    <Field label={label} className={className}>
      <Dropdown
        placeholder={t('plate_comparison.all')}
        value={value || t('plate_comparison.all')}
        selectedOptions={value ? [value] : []}
        onOptionSelect={(_e, data) => onChange(data.optionValue)}
      >
        <Option value="">{t('plate_comparison.all')}</Option>
        {options.map(v => <Option key={v} value={v}>{v}</Option>)}
      </Dropdown>
    </Field>
  );
}

export default function PlateComparison() {
  const s = useStyles();
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [lmdp, setLmdp] = useState([]);
  const [mdf, setMdf] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState({ 0: false, 1: false });
  const [search, setSearch] = useState('');
  const [mdfFinish, setMdfFinish] = useState(MDF_FINISHES[0].key);
  const [filters, setFilters] = useState({ color_tone: '', color: '', gloss: '', roughness: '', pattern: '', manufacturer: '' });
  const [selected, setSelected] = useState([]);

  const data = tab === 0 ? lmdp : mdf;

  useEffect(() => { document.title = `${t('plate_comparison.title')} | Fitit`; }, [t]);

  useEffect(() => {
    if (loaded[tab]) return;
    setLoading(true);
    fetch(tab === 0 ? `${API_BASE}/panels` : `${API_BASE}/mdf`)
      .then(r => r.json())
      .then(d => {
        if (tab === 0) setLmdp(Array.isArray(d) ? d : []);
        else setMdf(Array.isArray(d) ? d : []);
        setLoaded(p => ({ ...p, [tab]: true }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab, loaded]);

  const filtered = useMemo(() => {
    let d = data;
    if (search) {
      const se = search.toLowerCase();
      d = d.filter(i => i.code?.toLowerCase().includes(se) || i.name?.toLowerCase().includes(se));
    }
    if (tab === 0) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v) d = d.filter(i => i[k] === v);
      });
    }
    return d;
  }, [data, search, filters, tab]);

  const handleSelect = item => {
    if (selected.length >= 4) return;
    if (!selected.find(sel => sel.code === item.code)) setSelected(p => [...p, item]);
  };
  const handleRemove = item => setSelected(p => p.filter(sel => sel.code !== item.code));
  const resetFilters = () => { setSearch(''); setFilters({ color_tone: '', color: '', gloss: '', roughness: '', pattern: '', manufacturer: '' }); };

  return (
    <div className={s.page}>

      {/* Header */}
      <div className={s.headerBar}>
        <div className={s.container}>
          <h1 className={s.title}>{t('plate_comparison.title')}</h1>
          <p className={s.subtitle}>{t('plate_comparison.subtitle')}</p>

          {selected.length > 0 && (
            <div className={s.selectedRow}>
              <span className={s.selectedLabel}>{t('plate_comparison.selected_count', { count: selected.length })}</span>
              {selected.map(sel => (
                <span key={sel.code} className={s.chip}>
                  {sel.code}
                  <Button
                    appearance="transparent"
                    size="small"
                    shape="circular"
                    icon={<CloseIcon style={{ fontSize: 12 }} />}
                    onClick={() => handleRemove(sel)}
                    aria-label={t('plate_comparison.remove_aria', { code: sel.code })}
                    className={s.chipCloseBtn}
                  />
                </span>
              ))}
              <Button appearance="transparent" size="small" onClick={() => setSelected([])}>
                {t('plate_comparison.clear_all')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={s.body}>

        {/* Tabs */}
        <TabList
          selectedValue={tab}
          onTabSelect={(_e, data2) => { setTab(data2.value); setSelected([]); }}
          className={s.tabList}
        >
          <Tab value={0}>LMDP</Tab>
          <Tab value={1}>MDF</Tab>
        </TabList>

        {/* Filters */}
        <Card className={s.filtersCard} padding={tokens.spacingHorizontalM}>
          <div className={s.filtersRow}>
            <Field label={t('plate_comparison.search')} className={s.searchField}>
              <Input
                placeholder={t('plate_comparison.search_ph')}
                value={search}
                onChange={(_e, data2) => setSearch(data2.value)}
                contentBefore={<SearchIcon style={{ fontSize: 16 }} />}
              />
            </Field>

            {/* MDF finish selector */}
            {tab === 1 && (
              <Field label={t('plate_comparison.finish')} className={s.mdfField}>
                <Dropdown
                  value={t(MDF_FINISHES.find(f => f.key === mdfFinish)?.labelKey) || ''}
                  selectedOptions={[mdfFinish]}
                  onOptionSelect={(_e, data2) => setMdfFinish(data2.optionValue)}
                >
                  {MDF_FINISHES.map(f => <Option key={f.key} value={f.key}>{t(f.labelKey)}</Option>)}
                </Dropdown>
              </Field>
            )}

            {/* LMDP dropdowns */}
            {tab === 0 && LMDP_FILTERS.map(({ key, labelKey }) => (
              <FilterSelect
                key={key}
                label={t(labelKey)}
                value={filters[key]}
                options={getUniqueOptions(lmdp, key)}
                onChange={(v) => setFilters(p => ({ ...p, [key]: v }))}
                className={s.filterField}
              />
            ))}

            <Button appearance="subtle" size="small" icon={<FilterOffIcon style={{ fontSize: 15 }} />} onClick={resetFilters} className={s.resetBtn}>
              {t('plate_comparison.clear')}
            </Button>
          </div>
        </Card>

        {/* Compare panel */}
        <ComparePanel selected={selected} type={tab} mdfFinish={mdfFinish} onRemove={handleRemove} />

        {/* Status line */}
        <div className={s.statusLine}>
          <p className={s.statusText}>
            {loading ? t('plate_comparison.loading') : t('plate_comparison.found_count', { count: filtered.length })}
            {!loading && selected.length < 4 && ` · ${t('plate_comparison.click_to_select')}`}
          </p>
          {selected.length >= 4 && (
            <div className={s.maxNotice}>
              <InfoIcon style={{ fontSize: 14 }} />
              {t('plate_comparison.max_reached')}
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className={s.grid}>
            {Array.from({ length: 24 }).map((_, i) => (
              <Skeleton key={i} className={s.skeletonCard}>
                <SkeletonItem shape="square" className={s.skeletonImage} />
                <div className={s.skeletonLines}>
                  <SkeletonItem size={8} />
                  <SkeletonItem size={8} style={{ width: '60%' }} />
                </div>
              </Skeleton>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className={s.emptyWrap}>
            <EmptyState
              icon={<SearchOffIcon style={{ fontSize: 26 }} />}
              title={t('plate_comparison.no_results')}
              action={<Button appearance="outline" size="small" onClick={resetFilters}>{t('plate_comparison.clear_filters')}</Button>}
            />
          </div>
        ) : (
          <div className={s.grid}>
            {filtered.map((item, idx) => (
              <PlateCard
                key={item.id || item._id || idx}
                item={item}
                type={tab}
                mdfFinish={mdfFinish}
                selected={!!selected.find(sel => sel.code === item.code)}
                onSelect={handleSelect}
                onRemove={handleRemove}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
